import cx from 'classnames'
import { useId } from 'react'

import { artwork, brandNames } from './logo.artwork'
import css from './logo.module.css'
import type { LogoProps } from './logo.types'

/**
 * Derives the ARIA attributes for the logo from its `label`, like the
 * design system's Icon does.
 *
 * - Non-empty `label`: a meaningful image, `role="img"` + `aria-label`, and no
 *   `aria-hidden`, which would remove the logo and its name from the
 *   accessibility tree.
 * - Empty `label`: decorative, `aria-hidden="true"`.
 *
 * The result is spread before the consumer's props, so an explicit `role`,
 * `aria-label` or `aria-hidden` always wins.
 */
const getA11yProps = (
  label: string,
): { role: 'img'; 'aria-label': string } | { 'aria-hidden': true } =>
  label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true }

/**
 * The logos of the cinch and Marshall brands, drawn as inline SVG at their
 * Figma size.
 *
 * [View in Figma](https://www.figma.com/design/uoYtLe77tOoT3iN5hpDfv5?node-id=10189-1773)
 *
 * ## Usage
 *
 * Logo draws a brand's logo. Use it wherever the brand is identified, such as
 * headers, heroes and footers, and set `inverted` when it sits on a dark
 * background.
 *
 * ```tsx
 * import { Logo } from '@cinch-nx/feature-servicing'
 *
 * export const MyComponent = () => <Logo brand="cinchServicing" inverted />
 * ```
 *
 * ## Best Practices
 *
 * **Do**:
 * - Use `inverted` on dark backgrounds, including dark images, so the logo
 *   keeps its contrast.
 * - Resize the logo with a `className` that sets its `height`; the width
 *   follows the logo's proportions.
 * - Pass `label=""` when the brand name is already written next to the logo,
 *   so screen readers do not announce it twice.
 * - Keep the default label, or describe the destination, when the logo is
 *   the content of a link.
 *
 * **Don't**:
 * - Recolour the logo with CSS. The colours are the brand's, and the two
 *   variants are the only supported treatments.
 * - Set a width, in CSS or with `style`; the height stays at its own value,
 *   so the logo does not scale with it: a wider box adds empty space at the
 *   sides, and a narrower one shrinks the logo inside it. Set only a `height`.
 * - Draw one of these logos from an image or a font instead; this component
 *   keeps the artwork in step with Figma.
 */
export function Logo(props: LogoProps) {
  const {
    brand,
    inverted = false,
    label = brandNames[brand],
    className,
    ...svgProps
  } = props

  // A gradient is referenced by id, so each logo needs its own: one that
  // shares the id of an earlier, hidden logo loses its wordmark in Chrome and
  // Firefox.
  const gradientId = useId()
  const { width, height, render } = artwork[brand][inverted ? 'dark' : 'light']

  return (
    <svg
      className={cx(css.logo, className)}
      // Only IE and legacy Edge make an svg a focus stop without it; kept like
      // the Badge's icon.
      focusable="false"
      {...getA11yProps(label)}
      {...svgProps}
      // After the spread, so the Figma size cannot be replaced by an untyped
      // width or height prop.
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
    >
      {render(gradientId)}
    </svg>
  )
}

export default Logo
